import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Layers, AlertTriangle, Plus, Pencil, CheckCircle2, Trash2, X, Search, UserX, RefreshCw, Target } from "lucide-react";
import { C, eur, pct1 } from "../igualdad-data.js";
import { SectionHead, Status } from "../igualdad-components.jsx";
import { apiGet, apiPost, apiPut, apiDelete } from "../igualdad-api.js";
import { useIgualdad, puede } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Bandas salariales por puesto
   Listado por puesto (estadísticos internos, mercado, banda
   propuesta y versiones) y posicionamiento individual. Importes,
   amplitudes, posiciones, situaciones y avisos los calcula el
   backend: aquí solo se pintan, para que no haya dos fórmulas.
   ============================================================ */

const ETIQUETA_AVISO = {
  puesto_sin_vpt: "Puestos sin VPT",
  puesto_sin_banda: "Puestos sin banda aprobada vigente",
  puesto_sin_mercado: "Puestos sin referencias de mercado",
  inversion_punto_medio: "Punto medio invertido respecto a la VPT",
  solapamiento_bandas: "Solapamientos entre puestos consecutivos",
  persona_por_debajo: "Personas por debajo de banda",
  persona_por_encima: "Personas por encima de banda",
  persona_excluida: "Personas excluidas por datos incompletos",
  salario_sin_desglose: "Salario sin desglose por concepto",
};

const TONO_SITUACION = { por_debajo: C.danger, dentro: C.success, por_encima: "#d9682e" };

const FILTROS_SITUACION = [["", "Todas"], ["por_debajo", "Por debajo"], ["dentro", "Dentro"], ["por_encima", "Por encima"]];

const CAMPOS_IMPORTE = ["mercado_minimo", "mercado_p50", "mercado_maximo", "minimo", "punto_medio", "maximo"];
const CAMPOS_TEXTO = ["inicio_vigencia", "fin_vigencia", "mercado_fuente", "mercado_fecha", "justificacion", "observaciones"];

const MAX_AVISOS_VISIBLES = 5;

const euro = (n) => (n === null || n === undefined ? "—" : eur(n));
const pct = (n) => (n === null || n === undefined ? "—" : pct1(n));
const ratio = (n) => (n === null || n === undefined ? "—" : n.toFixed(2).replace(".", ","));
const fecha = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const vigencia = (b) => (b.inicio_vigencia
  ? `${fecha(b.inicio_vigencia)}${b.fin_vigencia ? ` – ${fecha(b.fin_vigencia)}` : " – sin fin"}`
  : "Sin fechas de vigencia");

/* La separación diseño / aprobación depende del permiso concreto. `es_staff`
   no sirve aquí: lo es cualquier usuario de Talention, también el consultor,
   que diseña pero no aprueba. */
function usePermisosBandas() {
  const { session } = useIgualdad();
  const permisos = session.user?.permisos || [];
  return {
    crear: puede("bandas_salariales.create", permisos),
    editar: puede("bandas_salariales.update", permisos),
    borrar: puede("bandas_salariales.delete", permisos),
    aprobar: puede("bandas_salariales.aprobar", permisos),
  };
}

/* Formulario a partir de una versión. En una revisión se copian los importes
   de la vigente pero no sus fechas: la nueva versión empieza cuando se decida. */
function formularioDesde(banda, { revision = false } = {}) {
  const form = {};
  for (const campo of [...CAMPOS_IMPORTE, ...CAMPOS_TEXTO]) form[campo] = banda?.[campo] ?? "";
  if (revision) { form.inicio_vigencia = ""; form.fin_vigencia = ""; }
  return form;
}

function cuerpoDesde(form) {
  const cuerpo = {};
  for (const campo of CAMPOS_IMPORTE) cuerpo[campo] = form[campo] === "" ? null : Number(form[campo]);
  for (const campo of CAMPOS_TEXTO) cuerpo[campo] = form[campo] === "" ? null : form[campo];
  return cuerpo;
}

function AvisosBandas({ avisos }) {
  const grupos = useMemo(() => {
    const porTipo = {};
    for (const aviso of avisos) (porTipo[aviso.tipo] ||= []).push(aviso);
    return Object.entries(porTipo);
  }, [avisos]);

  if (!grupos.length) return null;

  return (
    <div className="rb-card">
      <SectionHead icon={AlertTriangle} title="Controles de coherencia" />
      <div className="rb-grid-cards">
        {grupos.map(([tipo, lista]) => (
          <div key={tipo} className="rb-warn" data-testid={`aviso-${tipo}`}>
            <b>{ETIQUETA_AVISO[tipo] || tipo} · {lista.length}</b>
            <ul className="rb-alerts" style={{ marginTop: 8 }}>
              {lista.slice(0, MAX_AVISOS_VISIBLES).map((a, i) => <li key={i}>{a.mensaje}</li>)}
            </ul>
            {lista.length > MAX_AVISOS_VISIBLES && <div className="rb-note">y {lista.length - MAX_AVISOS_VISIBLES} más.</div>}
          </div>
        ))}
      </div>
      <div className="rb-note">Son avisos, no bloqueos: las excepciones se documentan en la justificación de la banda.</div>
    </div>
  );
}

function PersonasExcluidas({ excluidas }) {
  if (!excluidas.length) return null;

  return (
    <div className="rb-card rb-card-flat" data-testid="excluidas">
      <div style={{ padding: "14px 16px 0" }}><SectionHead icon={UserX} title={`Personas excluidas del cálculo · ${excluidas.length}`} /></div>
      <table className="rb-table rb-table-compact">
        <thead><tr><th className="rb-l">Persona</th><th className="rb-l">Puesto</th><th className="rb-l">Motivo</th></tr></thead>
        <tbody>
          {excluidas.map((p) => (
            <tr key={p.persona_id}>
              <td className="rb-l rb-strong">{p.nombre}</td>
              <td className="rb-l rb-soft">{p.puesto || "Sin puesto"}</td>
              <td className="rb-l">{p.motivos.map((m) => <div key={m.codigo}>{m.texto}</div>)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Campo({ id, etiqueta, errores, children }) {
  return (
    <div>
      <label htmlFor={id}>{etiqueta}</label>
      {children}
      {(errores?.[id] || []).map((m) => <div key={m} className="rb-formerr">{m}</div>)}
    </div>
  );
}

function FormularioBanda({ edicion, guardando, onCambio, onCancelar, onGuardar }) {
  const { puesto, banda, revision, form, errores, mensaje } = edicion;
  const importe = (id, etiqueta) => (
    <Campo id={id} etiqueta={etiqueta} errores={errores}>
      <input id={id} className="rb-inp" type="number" min="0" step="0.01" value={form[id]} onChange={(e) => onCambio(id, e.target.value)} />
    </Campo>
  );
  const fechaCampo = (id, etiqueta) => (
    <Campo id={id} etiqueta={etiqueta} errores={errores}>
      <input id={id} className="rb-inp" type="date" value={form[id]} onChange={(e) => onCambio(id, e.target.value)} />
    </Campo>
  );
  const titulo = banda ? `Editar borrador · versión ${banda.version}` : (revision ? "Revisar banda aprobada" : "Nueva banda");

  return (
    <div className="rb-modal-bg" onClick={onCancelar}>
      <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rb-modal-h">
          <div><div className="rb-modal-t">{titulo}</div><div className="rb-modal-s">{puesto.codigo} · {puesto.nombre}</div></div>
          <button className="rb-ico" onClick={onCancelar}><X size={18} /></button>
        </div>
        <div className="rb-modal-b">
          {revision && <p className="rb-note">Se creará una nueva versión en borrador. La aprobada no cambia hasta que se apruebe esta.</p>}
          <div className="rb-grid2-form">
            {fechaCampo("inicio_vigencia", "Inicio de vigencia")}
            {fechaCampo("fin_vigencia", "Fin de vigencia")}
            {importe("mercado_minimo", "Mínimo de mercado")}
            {importe("mercado_p50", "P50 de mercado")}
            {importe("mercado_maximo", "Máximo de mercado")}
            {fechaCampo("mercado_fecha", "Fecha de la referencia")}
            <div className="rb-grid2-full">
              <Campo id="mercado_fuente" etiqueta="Fuente de mercado" errores={errores}>
                <input id="mercado_fuente" className="rb-inp" value={form.mercado_fuente} onChange={(e) => onCambio("mercado_fuente", e.target.value)} />
              </Campo>
            </div>
            {importe("minimo", "Mínimo propuesto")}
            {importe("punto_medio", "Punto medio propuesto")}
            {importe("maximo", "Máximo propuesto")}
          </div>
          <Campo id="justificacion" etiqueta="Justificación" errores={errores}>
            <textarea id="justificacion" value={form.justificacion} onChange={(e) => onCambio("justificacion", e.target.value)} />
          </Campo>
          <Campo id="observaciones" etiqueta="Observaciones" errores={errores}>
            <textarea id="observaciones" value={form.observaciones} onChange={(e) => onCambio("observaciones", e.target.value)} />
          </Campo>
          {mensaje && <div className="rb-formerr">{mensaje}</div>}
        </div>
        <div className="rb-modal-f">
          <button className="rb-btn-ghost" onClick={onCancelar}>Cancelar</button>
          <button className="rb-btn" disabled={guardando} onClick={onGuardar}>{guardando ? "Guardando…" : "Guardar borrador"}</button>
        </div>
      </div>
    </div>
  );
}

function Confirmacion({ titulo, subtitulo, texto, error, boton, peligro = false, ocupado, onCancelar, onConfirmar }) {
  return (
    <div className="rb-modal-bg" onClick={onCancelar}>
      <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="rb-modal-h">
          <div><div className="rb-modal-t">{titulo}</div><div className="rb-modal-s">{subtitulo}</div></div>
          <button className="rb-ico" onClick={onCancelar}><X size={18} /></button>
        </div>
        <div className="rb-modal-b">
          <p className="rb-soft">{texto}</p>
          {error && <div className="rb-formerr">{error}</div>}
        </div>
        <div className="rb-modal-f">
          <button className="rb-btn-ghost" onClick={onCancelar}>Cancelar</button>
          <button className={peligro ? "rb-btn rb-btn-danger" : "rb-btn"} disabled={ocupado} onClick={onConfirmar}>{ocupado ? "Procesando…" : boton}</button>
        </div>
      </div>
    </div>
  );
}

/* Mensaje legible de un error de la API: los de validación traen detalle por campo. */
function mensajeDe(e, porDefecto) {
  if (e?.errors) return Object.values(e.errors).flat().join(" ");
  return e?.message || porDefecto;
}

function IgualdadBandasPuesto() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;
  const permiso = usePermisosBandas();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  const [edicion, setEdicion] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmacion, setConfirmacion] = useState(null);
  const [procesando, setProcesando] = useState(false);

  const cargar = useCallback(async () => {
    if (!politicaId) { setLoading(false); setData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet(`/politicas-retributivas/${politicaId}/bandas-salariales`);
      setData(res.data);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }, [politicaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const puestos = (data?.puestos || []).filter((p) => `${p.codigo} ${p.nombre}`.toLowerCase().includes(q.toLowerCase()));
  const resumen = data?.resumen || {};

  function abrirFormulario(puesto, { banda = null, revision = false } = {}) {
    const origen = banda || (revision ? puesto.banda_vigente : null);
    setEdicion({ puesto, banda, revision, form: formularioDesde(origen, { revision }), errores: null, mensaje: null });
  }

  async function guardar() {
    if (guardando || !edicion) return;
    setGuardando(true);
    const cuerpo = cuerpoDesde(edicion.form);
    try {
      if (edicion.banda) await apiPut(`/bandas-salariales/${edicion.banda.id}`, cuerpo);
      else await apiPost("/bandas-salariales", { politica_id: politicaId, puesto_id: edicion.puesto.puesto_id, ...cuerpo });
      setEdicion(null);
      fireToast("Borrador guardado.");
      await cargar();
    } catch (e) {
      setEdicion((ed) => ({ ...ed, errores: e.errors || null, mensaje: e.errors ? null : mensajeDe(e, "No se pudo guardar.") }));
    } finally { setGuardando(false); }
  }

  async function confirmar() {
    if (procesando || !confirmacion) return;
    setProcesando(true);
    const { accion, banda } = confirmacion;
    try {
      if (accion === "aprobar") await apiPost(`/bandas-salariales/${banda.id}/aprobar`);
      else await apiDelete(`/bandas-salariales/${banda.id}`);
      setConfirmacion(null);
      fireToast(accion === "aprobar" ? `Versión ${banda.version} aprobada.` : "Borrador eliminado.");
      await cargar();
    } catch (e) {
      setConfirmacion((c) => ({ ...c, error: mensajeDe(e, "No se pudo completar la acción.") }));
    } finally { setProcesando(false); }
  }

  return (
    <div className="rb-page">
      <div className="rb-pagehead">
        <div>
          <h1 className="rb-h1">Bandas salariales por puesto · {session.politica?.ejercicio}</h1>
          <p className="rb-lead">Retribución fija bruta anual a jornada completa (salario base y complementos), sin variable ni beneficios. La propuesta la decide quien diseña la banda y queda justificada.</p>
        </div>
      </div>

      {loading || error ? <Status loading={loading} error={error} /> : (
        <>
          <div className="rb-kpis" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <div className="rb-kpi"><div className="rb-kpi-l">Puestos</div><div className="rb-kpi-v">{resumen.puestos ?? 0}</div><div className="rb-kpi-s">{resumen.con_banda_vigente ?? 0} con banda vigente</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Personas analizadas</div><div className="rb-kpi-v">{resumen.personas_validas ?? 0}</div><div className="rb-kpi-s">con datos completos</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Personas excluidas</div><div className="rb-kpi-v">{resumen.personas_excluidas ?? 0}</div><div className="rb-kpi-s">sin puesto, jornada o fijo</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Fecha de referencia</div><div className="rb-kpi-v" style={{ fontSize: "21px" }}>{fecha(data?.fecha_referencia)}</div><div className="rb-kpi-s">vigencia de las bandas</div></div>
          </div>

          <div className="rb-toolbar">
            <div className="rb-search"><Search size={15} /><input placeholder="Buscar puesto…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="rb-toolbar-r"><span className="rb-count">{puestos.length} puestos</span></div>
          </div>

          <div className="rb-card rb-card-flat">
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table rb-table-compact">
                <thead>
                  <tr>
                    <th className="rb-l rb-fija-izq">Puesto</th><th>VPT</th><th>Ocupantes</th>
                    <th>Mín. actual</th><th>P25</th><th>Mediana</th><th>Media</th><th>P75</th><th>Máx. actual</th>
                    <th>Mín. mercado</th><th>P50 mercado</th><th>Máx. mercado</th><th className="rb-l">Fuente</th>
                    <th>Mín. propuesto</th><th>Punto medio</th><th>Máx. propuesto</th><th>Amplitud</th>
                    <th className="rb-l">Estado</th><th className="rb-l">Justificación</th><th className="rb-actions-h rb-fija-der"></th>
                  </tr>
                </thead>
                <tbody>
                  {puestos.map((p) => {
                    // La banda de trabajo: el borrador si lo hay, si no la vigente.
                    const banda = p.borrador || p.banda_vigente;
                    const est = p.estadisticos;
                    return (
                      <tr key={p.puesto_id}>
                        <td className="rb-l rb-fija-izq">
                          <div className="rb-strong">{p.nombre}</div>
                          <div className="rb-soft">{[p.codigo, p.area, p.grupo].filter(Boolean).join(" · ")}</div>
                        </td>
                        <td className="rb-mono">{p.pts > 0 ? p.pts : "—"}<div className="rb-soft">{p.escala || "Sin valorar"}</div></td>
                        <td className="rb-mono">{p.ocupantes_validos}</td>
                        <td className="rb-mono">{euro(est.minimo)}</td>
                        <td className="rb-mono">{euro(est.p25)}</td>
                        <td className="rb-mono">{euro(est.mediana)}</td>
                        <td className="rb-mono">{euro(est.media)}</td>
                        <td className="rb-mono">{euro(est.p75)}</td>
                        <td className="rb-mono">{euro(est.maximo)}</td>
                        <td className="rb-mono">{euro(banda?.mercado_minimo)}</td>
                        <td className="rb-mono">{euro(banda?.mercado_p50)}</td>
                        <td className="rb-mono">{euro(banda?.mercado_maximo)}</td>
                        <td className="rb-l rb-soft">{banda?.mercado_fuente || "—"}{banda?.mercado_fecha && <div>{fecha(banda.mercado_fecha)}</div>}</td>
                        <td className="rb-mono">{euro(banda?.minimo)}</td>
                        <td className="rb-mono rb-strong">{euro(banda?.punto_medio)}</td>
                        <td className="rb-mono">{euro(banda?.maximo)}</td>
                        <td className="rb-mono">{euro(banda?.amplitud_euros)}<div className="rb-soft">{pct(banda?.amplitud_pct)}</div></td>
                        <td className="rb-l">
                          {banda ? (
                            <>
                              <span className="rb-chip" style={{ background: banda.estado === "aprobada" ? "var(--success-bg)" : "var(--gray-100)", color: banda.estado === "aprobada" ? C.success : C.textMuted }}>{banda.estado_label} · v{banda.version}</span>
                              <div className="rb-soft">{vigencia(banda)}</div>
                              {p.borrador && p.banda_vigente && <div className="rb-soft">Vigente: v{p.banda_vigente.version}</div>}
                            </>
                          ) : <span className="rb-soft">Sin banda</span>}
                          {p.historial.length > 1 && (
                            <details>
                              <summary className="rb-soft">Historial · {p.historial.length} versiones</summary>
                              <ul className="rb-alerts">
                                {p.historial.map((v) => (
                                  <li key={v.id}>v{v.version} · {v.estado_label}{v.vigente ? " (vigente)" : ""} · {vigencia(v)} · {euro(v.minimo)} / {euro(v.punto_medio)} / {euro(v.maximo)}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </td>
                        <td className="rb-l rb-soft" style={{ minWidth: 220 }}>{banda?.justificacion || "—"}</td>
                        <td className="rb-actions rb-fija-der">
                          {p.borrador ? (
                            <>
                              {permiso.editar && <button className="rb-ico-btn" title="Editar borrador" aria-label="Editar borrador" onClick={() => abrirFormulario(p, { banda: p.borrador })}><Pencil size={15} /></button>}
                              {permiso.aprobar && <button className="rb-ico-btn" title="Aprobar" aria-label="Aprobar" onClick={() => setConfirmacion({ accion: "aprobar", puesto: p, banda: p.borrador, error: null })}><CheckCircle2 size={15} /></button>}
                              {permiso.borrar && <button className="rb-ico-btn rb-danger" title="Eliminar borrador" aria-label="Eliminar borrador" onClick={() => setConfirmacion({ accion: "borrar", puesto: p, banda: p.borrador, error: null })}><Trash2 size={15} /></button>}
                            </>
                          ) : permiso.crear && (
                            p.banda_vigente
                              ? <button className="rb-ico-btn" title="Revisar (nueva versión)" aria-label="Revisar" onClick={() => abrirFormulario(p, { revision: true })}><RefreshCw size={15} /></button>
                              : <button className="rb-ico-btn" title="Nueva banda" aria-label="Nueva banda" onClick={() => abrirFormulario(p)}><Plus size={15} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Solo con la respuesta en la mano: antes de elegir politica
                      la tabla esta vacia y el mensaje mentiria. */}
                  {data && !puestos.length && (
                    <tr><td className="rb-l rb-soft" colSpan={20}>
                      Este registro no tiene puestos, así que todavía no hay bandas que diseñar. El catálogo se mantiene en Organización · Puestos.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="rb-tablefoot">La tabla se desplaza en horizontal. El puesto y sus acciones quedan siempre a la vista.</div>
          </div>

          <AvisosBandas avisos={data?.avisos || []} />
          <PersonasExcluidas excluidas={data?.excluidas || []} />
        </>
      )}

      {edicion && (
        <FormularioBanda
          edicion={edicion}
          guardando={guardando}
          onCambio={(campo, valor) => setEdicion((ed) => ({ ...ed, form: { ...ed.form, [campo]: valor } }))}
          onCancelar={() => setEdicion(null)}
          onGuardar={guardar}
        />
      )}

      {confirmacion && (
        <Confirmacion
          titulo={confirmacion.accion === "aprobar" ? "Aprobar banda" : "Eliminar borrador"}
          subtitulo={`${confirmacion.puesto.nombre} · versión ${confirmacion.banda.version}`}
          texto={confirmacion.accion === "aprobar"
            ? "Una vez aprobada, la banda no se puede editar: cualquier cambio será una nueva versión. Si hay una versión vigente anterior, su vigencia terminará el día antes del inicio de esta."
            : "El borrador se elimina. Las versiones aprobadas no se tocan."}
          error={confirmacion.error}
          boton={confirmacion.accion === "aprobar" ? "Confirmar aprobación" : "Eliminar"}
          peligro={confirmacion.accion === "borrar"}
          ocupado={procesando}
          onCancelar={() => setConfirmacion(null)}
          onConfirmar={confirmar}
        />
      )}
    </div>
  );
}

function IgualdadPosicionamiento() {
  const { session } = useIgualdad();
  const politicaId = session.politicaId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [situacion, setSituacion] = useState("");

  const cargar = useCallback(async () => {
    if (!politicaId) { setLoading(false); setData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet(`/politicas-retributivas/${politicaId}/bandas-salariales/posicionamiento`);
      setData(res.data);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }, [politicaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const personas = (data?.personas || [])
    .filter((p) => !situacion || p.situacion === situacion)
    .filter((p) => `${p.nombre} ${p.puesto}`.toLowerCase().includes(q.toLowerCase()));
  const resumen = data?.resumen || {};
  const sinBanda = data?.sin_banda || [];

  const kpi = (clave, titulo, color) => (
    <div className="rb-kpi" data-testid={`resumen-${clave}`}><div className="rb-kpi-l">{titulo}</div><div className="rb-kpi-v" style={color ? { color } : undefined}>{resumen[clave] ?? 0}</div></div>
  );

  return (
    <div className="rb-page">
      <div className="rb-pagehead">
        <div>
          <h1 className="rb-h1">Posicionamiento en banda · {session.politica?.ejercicio}</h1>
          <p className="rb-lead">Cada persona frente a la banda aprobada y vigente de su puesto. La posición no se acota: por debajo de 0 % o por encima de 100 % señala una excepción que gestionar.</p>
        </div>
      </div>

      {loading || error ? <Status loading={loading} error={error} /> : (
        <>
          <div className="rb-kpis">
            {kpi("por_debajo", "Por debajo de banda", C.danger)}
            {kpi("dentro", "Dentro de banda", C.success)}
            {kpi("por_encima", "Por encima de banda", TONO_SITUACION.por_encima)}
            {kpi("sin_banda", "Sin banda vigente")}
            {kpi("excluidas", "Excluidas")}
          </div>

          <div className="rb-toolbar">
            <div className="rb-search"><Search size={15} /><input placeholder="Buscar persona o puesto…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="rb-seg">
              <span className="rb-seg-l"><Target size={12} /> Situación</span>
              {FILTROS_SITUACION.map(([valor, texto]) => (
                <button key={valor || "todas"} className={situacion === valor ? "on" : ""} onClick={() => setSituacion(valor)}>{texto}</button>
              ))}
            </div>
            <div className="rb-toolbar-r"><span className="rb-count">{personas.length} personas</span></div>
          </div>

          <div className="rb-card rb-card-flat">
            <div style={{ overflowX: "auto" }}>
              <table className="rb-table rb-table-compact">
                <thead>
                  <tr>
                    <th className="rb-l">Persona</th><th className="rb-l">Puesto</th><th>Salario normalizado</th>
                    <th>Mínimo</th><th>Punto medio</th><th>Máximo</th><th>Posición</th><th>Compa-ratio</th>
                    <th className="rb-l">Situación</th><th>Hasta el mínimo</th><th>Sobre el máximo</th>
                  </tr>
                </thead>
                <tbody>
                  {personas.map((p) => (
                    <tr key={p.persona_id}>
                      <td className="rb-l rb-strong">{p.nombre}</td>
                      <td className="rb-l rb-soft">{p.puesto}<div>v{p.banda_version}</div></td>
                      <td className="rb-mono rb-strong">{euro(p.salario)}</td>
                      <td className="rb-mono">{euro(p.minimo)}</td>
                      <td className="rb-mono">{euro(p.punto_medio)}</td>
                      <td className="rb-mono">{euro(p.maximo)}</td>
                      <td className="rb-mono">{pct(p.posicion_pct)}</td>
                      <td className="rb-mono">{ratio(p.compa_ratio)}</td>
                      <td className="rb-l"><span className="rb-chip" style={{ color: TONO_SITUACION[p.situacion], border: `1px solid ${TONO_SITUACION[p.situacion]}` }}>{p.situacion_label}</span></td>
                      <td className="rb-mono">{p.diferencia_hasta_minimo > 0 ? euro(p.diferencia_hasta_minimo) : "—"}</td>
                      <td className="rb-mono">{p.exceso_sobre_maximo > 0 ? euro(p.exceso_sobre_maximo) : "—"}</td>
                    </tr>
                  ))}
                  {!personas.length && <tr><td className="rb-l rb-soft" colSpan={11}>No hay personas posicionadas con estos filtros.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {sinBanda.length > 0 && (
            <div className="rb-card rb-card-flat" data-testid="sin-banda">
              <div style={{ padding: "14px 16px 0" }}><SectionHead icon={Layers} title={`Sin banda aprobada vigente · ${sinBanda.length}`} /></div>
              <table className="rb-table rb-table-compact">
                <thead><tr><th className="rb-l">Persona</th><th className="rb-l">Puesto</th><th>Salario normalizado</th></tr></thead>
                <tbody>
                  {sinBanda.map((p) => (
                    <tr key={p.persona_id}><td className="rb-l rb-strong">{p.nombre}</td><td className="rb-l rb-soft">{p.puesto}</td><td className="rb-mono">{euro(p.salario)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <PersonasExcluidas excluidas={data?.excluidas || []} />

          <div className="rb-note">
            Posicionamiento a {fecha(data?.fecha_referencia)} con los salarios actuales. La aplicación no reconstruye posicionamientos históricos: conserva las versiones de cada banda, pero no un historial salarial fechado con el que recalcular posiciones pasadas.
          </div>
        </>
      )}
    </div>
  );
}

export { IgualdadBandasPuesto, IgualdadPosicionamiento };
